import { useAppHistory } from "@faire/help-center-utils/AppHistory";
import { ArticleContextProvider } from "@faire/help-center-utils/hooks/ArticleContext";
import { getSettingsValues } from "@faire/help-center-utils/serialized-data/getSettingsValues";
import { SettingsContextProvider } from "@faire/help-center-utils/settings/useSetting";
import { DevTools } from "@faire/help-center/DevTools";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { hardRefresh } from "@faire/web--source/common/hardRefresh";
import { IntlProvider } from "@faire/web--source/common/localization/IntlProvider";
import { LocalizationStore } from "@faire/web--source/common/localization/LocalizationStore";
import { QueryClientProvider } from "@faire/web--source/common/reactQuery";
import { FontStyle } from "@faire/web--source/slate/Font/GlobalFontStyle";
import { DefaultTheme } from "@faire/web--source/slate/Theme";
import { ThemeProvider } from "@faire/web--source/slate/Theme/ThemeProvider";
import { FaireErrorBoundary } from "@faire/web--source/ui/FaireErrorBoundary/FaireErrorBoundary";
import { GlobalStyleResets } from "@faire/web--source/ui/GlobalStyleResets";
import { ObservableWindowStoreProvider } from "@faire/web--source/ui/hooks/useObservableWindow";
import { RoutingContext } from "@faire/web--source/ui/routing/RoutingContext";
import { Switch } from "@faire/web--source/ui/routing/Switch";
import supportHome, {
  route as supportHomeRoute,
} from "@faire/web-api--source/routes/www/support";
import * as React from "react";
import { Route, Router } from "react-router-dom";

import { AppRoutes } from "./AppRoutes";
import { HelpCenterHelmet } from "./components/HelpCenterHelmet/HelpCenterHelmet";

export const App: React.FC = () => {
  const settings = React.useMemo(() => getSettingsValues(), []);
  const { history } = useAppHistory(["history"]);
  return (
    <IntlProvider
      // locale and defaultLocale will always be the same since we're inlining translations
      localeKey={LocalizationStore.get().localeKey ?? DEFAULT_LOCALE_KEY}
      defaultLocale={LocalizationStore.get().locale}
      messages={{}}
    >
      <RoutingContext.Provider
        value={{
          locale: LocalizationStore.get().locale,
        }}
      >
        <FaireErrorBoundary logFatal fullPage history={history} helpCenterPage>
          <ObservableWindowStoreProvider>
            <FontStyle />
            <GlobalStyleResets />
            <ThemeProvider theme={DefaultTheme}>
              <ArticleContextProvider>
                <QueryClientProvider>
                  <SettingsContextProvider settings={settings}>
                    <HelpCenterHelmet />
                    <Router history={history}>
                      <Switch>
                        <Route path={supportHomeRoute} component={AppRoutes} />
                        <Route
                          component={() =>
                            hardRefresh("help-center", supportHome())
                          }
                        />
                      </Switch>
                    </Router>
                    <DevTools />
                  </SettingsContextProvider>
                </QueryClientProvider>
              </ArticleContextProvider>
            </ThemeProvider>
          </ObservableWindowStoreProvider>
        </FaireErrorBoundary>
      </RoutingContext.Provider>
    </IntlProvider>
  );
};
